import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { BacktestStrategySettings } from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import { pick } from 'es-toolkit';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { z } from 'zod';

/**
 * Strategy-settings keys that belong to the Exit step. The Setup step owns
 * everything else.
 */
export const EXIT_STRATEGY_KEYS = [
    'daysToHold',
    'timeStopBars',
    'takeProfitAtFraction',
    'stopAtFraction',
    'trailingStopAtFraction',
    'stopAtCurrencyAmount',
] as const satisfies readonly (keyof BacktestStrategySettings)[];

export type ExitStrategyKey = (typeof EXIT_STRATEGY_KEYS)[number];
export type ExitStrategySettings = Pick<BacktestStrategySettings, ExitStrategyKey>;

export interface ExitStepState {
    strategy: ExitStrategySettings;
}

export type ValidatedExitFields = 'exitMethod';

function createInitialTouched(): Record<ValidatedExitFields, boolean> {
    return { exitMethod: false };
}

interface ExitReducerState {
    form: ExitStepState;
    touched: Record<ValidatedExitFields, boolean>;
}

type ExitAction =
    | { type: 'PATCH_STRATEGY'; partial: Partial<ExitStrategySettings> }
    | { type: 'MARK_TOUCHED'; field: ValidatedExitFields }
    | { type: 'RESET' };

function createInitialReducerState(): ExitReducerState {
    return { form: createInitialExitState(), touched: createInitialTouched() };
}

/** Build a fresh exit state with spec-aligned defaults. */
export function createInitialExitState(): ExitStepState {
    return {
        strategy: {
            daysToHold: [0],
            timeStopBars: undefined,
            takeProfitAtFraction: [0.05],
            stopAtFraction: [0.05],
            trailingStopAtFraction: undefined,
            stopAtCurrencyAmount: undefined,
        },
    };
}

function exitReducer(state: ExitReducerState, action: ExitAction): ExitReducerState {
    switch (action.type) {
        case 'PATCH_STRATEGY':
            return {
                ...state,
                form: { ...state.form, strategy: { ...state.form.strategy, ...action.partial } },
            };
        case 'MARK_TOUCHED':
            if (state.touched[action.field]) return state;
            return { ...state, touched: { ...state.touched, [action.field]: true } };
        case 'RESET':
            return createInitialReducerState();
        default:
            throw new UnreachableCaseError(action);
    }
}

/** Extract the Exit-owned subset from a full BacktestStrategySettings. */
export function pickExitStrategy(strategy: BacktestStrategySettings): ExitStrategySettings {
    return pick(strategy, EXIT_STRATEGY_KEYS as unknown as ExitStrategyKey[]);
}

const strategyShape = BacktestStrategySettings.shape;

const ExitStrategySchema = z
    .object({
        daysToHold: strategyShape.daysToHold,
        timeStopBars: strategyShape.timeStopBars,
    })
    .refine((s) => !!s.daysToHold?.length || !!s.timeStopBars?.length, {
        message: 'Specify at least one exit timing',
    });

/**
 * Compute per-field validation errors for an Exit step state. Pure: takes the
 * full state in, returns a message-or-null for every validated field.
 */
export function validateExitState(state: ExitStepState): Record<ValidatedExitFields, string | null> {
    const result = ExitStrategySchema.safeParse(state.strategy);
    return {
        exitMethod: result.success ? null : (result.error.issues[0]?.message ?? null),
    };
}

/**
 * Exit pane signal model. Owns the exit-related strategy fields. The
 * cross-field "at least one exit method" invariant is surfaced via fieldErrors
 * instead of being enforced in the reducer, so daysToHold and timeStopBars can
 * coexist freely.
 */
export class ExitStepModel {
    readonly #state = signal<ExitReducerState>(createInitialReducerState());
    readonly #lastSavedJson = signal<string>(JSON.stringify(this.#state.peek().form));

    readonly state: ReadonlySignal<ExitStepState> = computed(() => this.#state.value.form);
    readonly strategy: ReadonlySignal<ExitStrategySettings> = computed(() => this.#state.value.form.strategy);

    readonly fieldErrors: ReadonlySignal<Record<ValidatedExitFields, string | null>> = computed(() =>
        validateExitState(this.#state.value.form),
    );

    readonly validationErrors: ReadonlySignal<string[]> = computed(() =>
        Object.values(this.fieldErrors.value).filter((m): m is string => m != null),
    );
    readonly isValid: ReadonlySignal<boolean> = computed(() => this.validationErrors.value.length === 0);

    readonly isDirty: ReadonlySignal<boolean> = computed(
        () => JSON.stringify(this.#state.value.form) !== this.#lastSavedJson.value,
    );

    readonly touched: ReadonlySignal<Record<ValidatedExitFields, boolean>> = computed(() => this.#state.value.touched);

    markTouched(field: ValidatedExitFields): void {
        this.#dispatch({ type: 'MARK_TOUCHED', field });
    }

    patchStrategy(partial: Partial<ExitStrategySettings>): void {
        this.#dispatch({ type: 'PATCH_STRATEGY', partial });
    }

    #dispatch(action: ExitAction): void {
        this.#state.value = exitReducer(this.#state.value, action);
    }

    hydrate(form: ExitStepState): void {
        this.#state.value = { form, touched: createInitialTouched() };
        this.#lastSavedJson.value = JSON.stringify(form);
    }

    markSaved(): void {
        this.#lastSavedJson.value = JSON.stringify(this.#state.value.form);
    }

    reset(): void {
        this.#dispatch({ type: 'RESET' });
        this.#lastSavedJson.value = JSON.stringify(this.#state.peek().form);
    }
}
